import { useState } from 'react';
import { Loader2, Sparkles, RefreshCw, Trash2, MessageSquareQuote, AlertTriangle, Sun, HeartHandshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { adminGenerateFramePoses, adminClearFramePoses } from '@/lib/api';
import { PoseFigure } from '@/components/scout/PoseFigure';
import { POSES } from '@/constants/testIds';

function PoseCard({ pose, frameIdx }) {
  return (
    <article data-testid={POSES.card(frameIdx, pose.idx)} className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0B0B0C] p-3">
      <div className="flex gap-3">
        <div className="shrink-0 w-24 sm:w-28 rounded-lg bg-[#141416] p-1.5 flex items-center justify-center">
          <PoseFigure figures={pose.figures} testId={POSES.figureSvg(frameIdx, pose.idx)} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))]">Pose {pose.idx + 1}</p>
          <h5 data-testid={POSES.name(frameIdx, pose.idx)} className="font-display text-lg leading-tight text-[hsl(var(--lxra-fg))] mt-0.5">{pose.name}</h5>
          {pose.summary && <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-1 leading-snug">{pose.summary}</p>}
        </div>
      </div>
      {pose.subjects?.length > 0 && (
        <dl className="mt-3 space-y-1.5">
          {pose.subjects.map((s) => (
            <div key={s.label} className="rounded-lg bg-[#141416] px-2.5 py-2 text-[11px]">
              <dt className="uppercase tracking-[0.1em] text-[10px] text-[hsl(var(--lxra-muted))]">{s.label}</dt>
              <dd className="text-[hsl(var(--lxra-fg))]/90 leading-snug mt-0.5">
                {[s.stance, s.weight].filter(Boolean).join(' · ')}
                {s.hands && <><br /><span className="text-[hsl(var(--lxra-muted))]">Hands:</span> {s.hands}</>}
                {s.head_eyes && <><br /><span className="text-[hsl(var(--lxra-muted))]">Chin & eyes:</span> {s.head_eyes}</>}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {pose.cue && (
        <p data-testid={POSES.cue(frameIdx, pose.idx)} className="mt-3 flex gap-2 rounded-lg border-l-2 border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))]/[0.06] px-3 py-2 text-xs italic leading-snug text-[hsl(var(--lxra-fg))]">
          <MessageSquareQuote className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--lxra-gold))] mt-0.5" /> <span>&ldquo;{pose.cue}&rdquo;</span>
        </p>
      )}
      <div className="mt-2 space-y-1 text-[11px] leading-snug">
        {pose.light_note && <p className="flex gap-1.5 text-[hsl(var(--lxra-muted))]"><Sun className="h-3 w-3 shrink-0 mt-0.5 text-[hsl(var(--lxra-gold))]" /> {pose.light_note}</p>}
        {pose.avoid && <p className="flex gap-1.5 text-amber-200/90"><AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" /> Avoid: {pose.avoid}</p>}
      </div>
    </article>
  );
}

export const PosesTab = ({ frame, reportId, comfortNotes, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const poses = frame.poses;
  const generating = busy || poses?.status === 'generating';
  const subjectCount = frame.blocking?.subjects?.length || 1;

  const generate = async () => {
    setBusy(true);
    try {
      await adminGenerateFramePoses(reportId, frame.idx);
      toast.success(`Three poses ready for “${frame.title}”.`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate poses.');
      onChanged();
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try { await adminClearFramePoses(reportId, frame.idx); toast.success('Poses cleared.'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear poses.'); }
    finally { setBusy(false); }
  };

  if (generating) {
    return (
      <div data-testid={POSES.loading(frame.idx)} className="space-y-3" aria-busy="true">
        <p className="flex items-center gap-2 text-xs text-[hsl(var(--lxra-muted))]"><Loader2 className="h-3.5 w-3.5 animate-spin text-[hsl(var(--lxra-gold))]" /> Directing {subjectCount === 1 ? 'the pose' : `${subjectCount} people`} for {frame.time}… usually 10–20 s.</p>
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-xl bg-[#141416]" />)}
      </div>
    );
  }

  if (!poses?.items?.length) {
    return (
      <div data-testid={POSES.empty(frame.idx)} className="rounded-xl border border-dashed border-[hsl(var(--lxra-border))] p-4 text-center">
        <HeartHandshake className="h-5 w-5 mx-auto text-[hsl(var(--lxra-gold))]" />
        <p className="mt-2 text-xs text-[hsl(var(--lxra-fg))]">Three poses for this exact moment — shaped by the {frame.photo?.lens ? frame.photo.lens.split(' ').slice(-2).join(' ') : 'lens'}, the {frame.sun?.direction ? `${frame.sun.direction} light` : 'light'} and {subjectCount === 1 ? 'one subject' : `${subjectCount} subjects`}.</p>
        <p className="mt-1 text-[11px] text-[hsl(var(--lxra-muted))]">{comfortNotes ? 'Your comfort notes will be honoured.' : 'Tip: add client comfort notes above so poses respect them.'}</p>
        {poses?.status === 'error' && <p data-testid={POSES.error(frame.idx)} className="mt-2 text-[11px] text-red-300">{poses.error}</p>}
        <Button size="sm" onClick={generate} data-testid={POSES.generate(frame.idx)} className="mt-3 h-8 bg-[hsl(var(--lxra-gold))] px-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))]"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate poses</Button>
        <p className="mt-1.5 text-[10px] text-[hsl(var(--lxra-muted))]">1 AI text call · no image credits</p>
      </div>
    );
  }

  return (
    <div data-testid={POSES.list(frame.idx)} className="space-y-3">
      {poses.items.map((p) => <PoseCard key={p.idx} pose={p} frameIdx={frame.idx} />)}
      {poses.status === 'error' && <p data-testid={POSES.error(frame.idx)} className="text-[11px] text-red-300">Last regenerate failed: {poses.error}. Showing the previous set.</p>}
      <div className="flex items-center justify-between gap-2 pt-1">
        <p className="text-[10px] text-[hsl(var(--lxra-muted))]">{poses.comfort_notes_used ? 'Comfort notes applied' : 'No comfort notes at generation time'}{poses.model ? ` · ${poses.model}` : ''}</p>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={generate} data-testid={POSES.regenerate(frame.idx)} className="h-7 px-2 text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]"><RefreshCw className="h-3 w-3 mr-1" /> Redo</Button>
          <Button size="icon" variant="ghost" onClick={clear} data-testid={POSES.clear(frame.idx)} title="Clear poses" className="h-7 w-7 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10"><Trash2 className="h-3 w-3" /></Button>
        </div>
      </div>
    </div>
  );
};
